// Send work on the Mission Panel card (#6770, epic #6552; prototype sendHTML,
// `docs/design/fleet-redesign/prototype.html` `:2226-2278`) as pure data: the recognised
// line, the nine role chips and why each is off-limits, the button's words, a server refusal
// in the console's words, and where a sent job lives once the server has minted it.
// `components/machine-card/SendWork.tsx` renders it; nothing here reads a signal or the url.
//
// EVERY VERDICT IS THE SERVER'S (#6767, `POST /v1/runners/:id/work`). The link is read by the
// ONE parser the route reads (`parseWorkLink`) and the chips by the route's own table
// (`SEND_ROLES`, `sendRoleVerdict`), so the form can never offer a send the route's role
// table refuses; whether the repo is connected, the item open or already on the machine is
// only ever the route's answer, shown in the recognised line. The prototype's browser checks
// (`ACCEPTS`, a hardcoded owner, sendState's duplicate test) are not copied.
//
// A 409/422 REFUSAL IS KEYED BY ITS `reason`. The dashboard cannot import `@developerz/mcp`,
// so `SEND_REFUSAL_KEY` mirrors `BoxSendRefusalReason` (`packages/mcp/src/box-send.ts`) by name,
// pinned in `send-work.test.ts`; other statuses, a reason this build does not know, or a
// problem with none show the server's sentence verbatim.
//
// A SENT JOB LIVES IN TWO READS (Sending step 6): the page's task queue, whose pending rows
// targeted at a machine read Starting on its card, and one `useTaskDetail` per id this page
// sent (`./send-work-page.ts`), which also tells when the 10-minute hard target released it
// (`TARGET_RELEASE_MS`) so the card can say the job went back to the open queue.

import {
  type AgentModeKey,
  MACHINE_ROLE_CARD_ORDER,
  type MachineRoleView,
  parseWorkLink,
  SEND_ROLES,
  type SendRoleRefusal,
  sendRoleVerdict,
  type TaskStatus,
  type WorkLink,
  type WorkLinkKind,
} from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import { ApiError, errorReason } from './api-errors';
import type { CardState } from './machine-card';

/** The route's `link` cap (`SendWorkBodySchema`, `.max(2_000)`). */
export const SEND_LINK_MAX = 2_000;
/** The route's note cap (`BOX_SEND_NOTE_MAX`, `@developerz/mcp`), the prototype's `maxlength`. */
export const SEND_NOTE_MAX = 500;
/** How long the back-to-the-queue note stays on the card. */
export const RELEASE_NOTE_MS = 7_000;

/** Each chip's name: the card's names, equal to `MACHINE_ROLE_NAME` in en ("Coder" is `worker`). */
export const SEND_ROLE_LABEL: Readonly<Record<AgentModeKey, string>> = {
  worker: 'fleet.send.role_worker',
  reviewer: 'fleet.send.role_reviewer',
  ci_fixer: 'fleet.send.role_ci_fixer',
  planner: 'fleet.send.role_planner',
  scout: 'fleet.send.role_scout',
  triage: 'fleet.send.role_triage',
  devops: 'fleet.send.role_devops',
  gardener: 'fleet.send.role_gardener',
  escalation: 'fleet.send.role_escalation',
};

/** Each enabled chip's `title`: the prototype's `CAPS` descriptions (`:1314-1324`). */
export const SEND_ROLE_DESC: Readonly<Record<AgentModeKey, string>> = {
  worker: 'fleet.send.role_desc_worker',
  reviewer: 'fleet.send.role_desc_reviewer',
  ci_fixer: 'fleet.send.role_desc_ci_fixer',
  planner: 'fleet.send.role_desc_planner',
  scout: 'fleet.send.role_desc_scout',
  triage: 'fleet.send.role_desc_triage',
  devops: 'fleet.send.role_desc_devops',
  gardener: 'fleet.send.role_desc_gardener',
  escalation: 'fleet.send.role_desc_escalation',
};

/**
 * A reason whose copy has one form per what the ROLE takes (never the pasted link's kind:
 * an `issues/<n>` link whose number is a pull request is refused `role_wrong_kind` for an
 * issue role, in its "doesn't take pull requests" form).
 */
type ByTakes = {
  /** A role that takes neither; absent where no such role reaches the reason. */
  readonly neither?: string;
  /** A role that takes pull requests (Reviewer). */
  readonly pr: string;
  /** A role that takes issues (Coder, Scout, Escalation). */
  readonly issue: string;
};

/** Every refusal `box send` answers (`BoxSendRefusalReason`), to its copy's key or keys. */
export const SEND_REFUSAL_KEY = {
  link_malformed: 'fleet.send.refused_link_malformed',
  role_wrong_kind: {
    neither: 'fleet.send.refused_role_wrong_kind_neither',
    pr: 'fleet.send.refused_role_wrong_kind_issue',
    issue: 'fleet.send.refused_role_wrong_kind_pr',
  },
  role_not_on_machines: 'fleet.send.refused_role_not_on_machines',
  machine_offline: 'fleet.send.refused_machine_offline',
  presence_unknown: 'fleet.send.refused_presence_unknown',
  repo_not_connected: 'fleet.send.refused_repo_not_connected',
  role_off: 'fleet.send.refused_role_off',
  link_unreadable: {
    pr: 'fleet.send.refused_link_unreadable_pr',
    issue: 'fleet.send.refused_link_unreadable_issue',
  },
  issue_closed: 'fleet.send.refused_issue_closed',
  already_on_machine: {
    pr: 'fleet.send.refused_already_on_machine_pr',
    issue: 'fleet.send.refused_already_on_machine_issue',
  },
  review_switched_off: 'fleet.send.refused_review_switched_off',
  review_not_entitled: 'fleet.send.refused_review_not_entitled',
  pr_merged: 'fleet.send.refused_pr_merged',
  pr_closed: 'fleet.send.refused_pr_closed',
  review_in_flight: 'fleet.send.refused_review_in_flight',
  review_posting: 'fleet.send.refused_review_posting',
  nothing_reviewable: 'fleet.send.refused_nothing_reviewable',
  model_budget_exhausted: 'fleet.send.refused_model_budget_exhausted',
  already_reviewed: 'fleet.send.refused_already_reviewed',
} as const satisfies Readonly<Record<string, string | ByTakes>>;
export type SendRefusalReason = keyof typeof SEND_REFUSAL_KEY;

/** The reasons whose copy names no repo or number, so a refusal with no parsed link keys too. */
const LINKLESS: ReadonlySet<SendRefusalReason> = new Set<SendRefusalReason>([
  'link_malformed',
  'role_wrong_kind',
  'role_not_on_machines',
  'machine_offline',
  'presence_unknown',
  'role_off',
  'review_not_entitled',
  'model_budget_exhausted',
]);

const isReason = (v: unknown): v is SendRefusalReason =>
  typeof v === 'string' && Object.hasOwn(SEND_REFUSAL_KEY, v);
const isRole = (v: unknown): v is AgentModeKey =>
  typeof v === 'string' && Object.hasOwn(SEND_ROLES, v);

/** The key a refusal's copy takes for this role, or null when its form is not one a role reaches. */
function refusalKey(reason: SendRefusalReason, role: AgentModeKey): string | null {
  const keyed: string | ByTakes = SEND_REFUSAL_KEY[reason];
  if (typeof keyed === 'string') return keyed;
  const takes = SEND_ROLES[role].takes;
  if (takes.length === 0) return keyed.neither ?? null;
  return takes.includes('pr') ? keyed.pr : keyed.issue;
}

/**
 * What the recognised line says after a 409/422 refusal: the reason's copy, keyed and
 * interpolated (`{role}`, `{repo}`, `{number}` off the pasted link), or the server's own
 * sentence for other statuses, a reason with no key, no reason, an unknown role, or a
 * role the copy has no form for.
 */
export function refusalLine(err: unknown, link: WorkLink | null, tr: Translator): string {
  const server = errorReason(err, tr('fleet.send.failed'));
  if (!(err instanceof ApiError) || (err.status !== 409 && err.status !== 422)) return server;
  const reason = err.extensions?.['reason'];
  const role = err.extensions?.['role'];
  if (!isReason(reason) || !isRole(role)) return server;
  if (link === null && !LINKLESS.has(reason)) return server;
  const key = refusalKey(reason, role);
  if (key === null) return server;
  return tr(key, {
    role: tr(SEND_ROLE_LABEL[role]),
    repo: link?.repo ?? '',
    number: link === null ? '' : String(link.number),
  });
}

/** The recognised line's three resting states, off the one parser. */
export type LinkRead =
  | { readonly kind: 'empty' }
  | { readonly kind: 'malformed' }
  | { readonly kind: 'link'; readonly link: WorkLink };

export function readLink(text: string): LinkRead {
  if (text.trim() === '') return { kind: 'empty' };
  const link = parseWorkLink(text);
  return link === null ? { kind: 'malformed' } : { kind: 'link', link };
}

/** One role chip: enabled, or disabled with the FIRST reason that holds as its `title`. */
export type SendChip = {
  readonly key: AgentModeKey;
  readonly label: string;
  readonly title: string;
  readonly disabled: boolean;
  /** " off" beside the name: the role is off here and that is its SOLE disabled reason. */
  readonly off: boolean;
};

/** The role table's refusal of the LINK's kind, as a chip states it. */
function tableReasonKey(verdict: SendRoleRefusal, role: AgentModeKey, kind: WorkLinkKind): string {
  if (verdict === 'role_not_on_machines') return 'fleet.send.refused_role_not_on_machines';
  if (SEND_ROLES[role].takes.length === 0) return 'fleet.send.refused_role_wrong_kind_neither';
  return kind === 'pr'
    ? 'fleet.send.refused_role_wrong_kind_pr'
    : 'fleet.send.refused_role_wrong_kind_issue';
}

/**
 * The nine chips in the card's order (`MACHINE_ROLE_CARD_ORDER`). A chip is enabled exactly
 * when a link is recognised and the route's table takes it (`sendRoleVerdict`: takes
 * nothing, then no machine lane, then the wrong kind, in that order) and its role is not off.
 * Off is read only from the machine's effective roles (#6757): `effective === false`; with
 * no roles read (another org's or a user's machine) no chip reads off, and the route's
 * `role_off` refusal still backs it.
 */
export function sendChips(
  link: WorkLink | null,
  roles: readonly MachineRoleView[] | null | undefined,
  tr: Translator,
): readonly SendChip[] {
  return MACHINE_ROLE_CARD_ORDER.map((key) => {
    const label = tr(SEND_ROLE_LABEL[key]);
    const chip = (title: string, disabled: boolean, off = false): SendChip => ({
      key,
      label,
      title,
      disabled,
      off,
    });
    if (link === null) return chip(tr('fleet.send.paste_first'), true);
    const verdict = sendRoleVerdict(key, link.kind);
    if (verdict !== null)
      return chip(tr(tableReasonKey(verdict, key, link.kind), { role: label }), true);
    if (roles?.find((r) => r.key === key)?.effective === false) {
      return chip(tr('fleet.send.refused_role_off', { role: label }), true, true);
    }
    return chip(tr(SEND_ROLE_DESC[key]), false);
  });
}

/** The pick, while its chip is enabled; a link that no longer allows it un-picks it. */
export function chosenRole(
  chips: readonly SendChip[],
  picked: AgentModeKey | null,
): AgentModeKey | null {
  if (picked === null) return null;
  return chips.some((c) => c.key === picked && !c.disabled) ? picked : null;
}

/** The send button's words (sendBtnLabel, `:2251`). */
export function sendButtonLabel(
  role: AgentModeKey | null,
  sending: boolean,
  tr: Translator,
): string {
  if (sending) return tr('fleet.send.button_sending');
  if (role === null) return tr('fleet.send.button_pick');
  return tr('fleet.send.button_send', { role: tr(SEND_ROLE_LABEL[role]) });
}

/** "It starts when this machine runs again." on a paused or blocked machine. */
export const startsLater = (state: CardState): boolean => state === 'paused' || state === 'blocked';

/** The form's fields and its one in-flight send. A refusal keeps every field. */
export type SendForm = {
  readonly link: string;
  readonly role: AgentModeKey | null;
  readonly note: string;
  readonly sending: boolean;
  /** The last refusal's sentence, shown in the recognised line until the next edit. */
  readonly refusal: string | null;
};

export const EMPTY_SEND_FORM: SendForm = {
  link: '',
  role: null,
  note: '',
  sending: false,
  refusal: null,
};

/** A keystroke or a pick: the line goes back to reading the link. */
export const editForm = (
  f: SendForm,
  patch: Partial<Pick<SendForm, 'link' | 'role' | 'note'>>,
): SendForm => ({ ...f, ...patch, refusal: null });

export const sendingForm = (f: SendForm): SendForm => ({ ...f, sending: true, refusal: null });

export const refusedForm = (f: SendForm, refusal: string): SendForm => ({
  ...f,
  sending: false,
  refusal,
});

/** "PR #N sent to <Role> on <machine>" (the prototype's `say`, `:2737`). */
export function sentAnnouncement(
  link: WorkLink,
  role: AgentModeKey,
  machine: string,
  tr: Translator,
): string {
  return tr(link.kind === 'pr' ? 'fleet.send.sent_pr' : 'fleet.send.sent_issue', {
    number: String(link.number),
    role: tr(SEND_ROLE_LABEL[role]),
    machine,
  });
}

// ─── Where a sent job lives ──────────────────────────────────────────────────

/** What the derivation reads of a task: the queue row, a per-id read, or the 201. */
export type SentRow = {
  readonly status: TaskStatus;
  /** Nullish on read: an api older than #6767 serves none, which never reads as Starting. */
  readonly target_runner_id?: string | null;
  readonly runner_id: string | null;
  readonly title: string;
};

/** One task this page sent: where it went, what it named, and its newest read. */
export type SentTask = {
  readonly id: string;
  readonly boxId: string;
  readonly kind: WorkLinkKind;
  readonly number: number;
  /** The per-id read once it answers; the 201's task until then. */
  readonly row: SentRow;
};

/** A Starting row on a card: the real task's own title (deviation 3), never `taskLine`. */
export type StartingRow = { readonly id: string; readonly title: string };

/**
 * Where a sent task stands for the card it was sent to. `starting`: pending, held for this
 * machine. `released`: its target cleared while pending, or another machine claimed it.
 * `claimed`: this machine took it (the card's current job now). `ended`: past the claim
 * window in any other status, so it is no longer this form's to follow. `waiting`: pending
 * with no target read (an older api), which claims nothing.
 */
export type SentVerdict = 'starting' | 'released' | 'claimed' | 'ended' | 'waiting';

export function sentVerdict(boxId: string, row: SentRow): SentVerdict {
  if (row.status === 'pending') {
    if (row.target_runner_id === boxId) return 'starting';
    return row.target_runner_id === null ? 'released' : 'waiting';
  }
  if (row.status === 'assigned' || row.status === 'in_progress') {
    return row.runner_id === boxId ? 'claimed' : 'released';
  }
  return 'ended';
}

/**
 * The card's Starting rows: this page's sent tasks still held for it (their per-id read
 * decides), then the queue's pending rows targeted at it, deduped by id, without ANY task
 * the machine already lists as a job (`jobs[]`, #6745, pinned `pending` rows included), so
 * one task never shows twice on a card.
 */
export function startingRows(
  boxId: string,
  queue: readonly (SentRow & { readonly id: string })[],
  sent: readonly SentTask[],
  jobIds: readonly string[],
): readonly StartingRow[] {
  const out = new Map<string, string>();
  const followed = new Set(sent.map((s) => s.id));
  for (const s of sent) {
    if (s.boxId === boxId && sentVerdict(boxId, s.row) === 'starting') out.set(s.id, s.row.title);
  }
  for (const r of queue) {
    if (followed.has(r.id) || out.has(r.id)) continue;
    if (r.status === 'pending' && r.target_runner_id === boxId) out.set(r.id, r.title);
  }
  for (const id of jobIds) out.delete(id);
  return [...out].map(([id, title]) => ({ id, title }));
}

/** The sent tasks this card should say went back to the open queue. */
export function releasedTasks(boxId: string, sent: readonly SentTask[]): readonly SentTask[] {
  return sent.filter((s) => s.boxId === boxId && sentVerdict(boxId, s.row) === 'released');
}

/** "PR #N went back to the open queue. …" (new copy, deviation 2). */
export function releaseNote(task: SentTask, tr: Translator): string {
  return tr(task.kind === 'pr' ? 'fleet.send.released_pr' : 'fleet.send.released_issue', {
    number: String(task.number),
  });
}

/** Whether the page stops following a sent task: noted, claimed by its machine, or past the claim. */
export const sentSettled = (s: SentTask): boolean => {
  const v = sentVerdict(s.boxId, s.row);
  return v === 'claimed' || v === 'ended';
};
